'use client'

import Link from 'next/link'

import { Bone } from '@/components/ui/bone'
import { ContentImage } from '@/components/ui/content-image'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

import { showsCommercialBadge, type PortfolioProject } from './portfolio-types'

interface PortfolioCardProps {
  project: PortfolioProject
  detailBasePath: string
}

/**
 * Shared project card for /gallery and /projects. Card surfaces on paper, no
 * shadow (spec 3.3: --surface-shadow is none). Hover uses an accent hairline
 * border, never accent text on light (teal pairing rule, spec 3.1): the only
 * accent fill on the card is the featured badge and the hover "View Project"
 * pill, both paired with white text.
 */
export function PortfolioCard({ project, detailBasePath }: PortfolioCardProps) {
  const slug = project.slug || project.id
  const mediaCount = project.media?.length || 0
  const isFeatured = project.isFeatured || project.isSponsored
  const showCommercial = showsCommercialBadge(project)
  const year = project.createdAt ? new Date(project.createdAt).getUTCFullYear() : null

  return (
    <Link
      href={`${detailBasePath}/${slug}`}
      className={cn(
        'group block overflow-hidden rounded-2xl border border-border bg-surface transition-colors',
        'hover:border-accent',
        isFeatured && 'sm:col-span-2'
      )}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-surface-secondary">
        <ContentImage
          src={project.media?.[0]?.url}
          alt={project.title || 'Project'}
          contentType="PROJECT"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          fallbackSrc="/images/placeholder-gallery.jpg"
        />
        {(project.categoryName || showCommercial) && (
          <div className="absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
            {project.categoryName && (
              <span className="rounded-full bg-black/50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[1px] text-white backdrop-blur-sm">
                {project.categoryName}
              </span>
            )}
            {showCommercial && (
              <span className="rounded-full bg-accent px-2.5 py-1 text-[10px] font-bold uppercase tracking-[1px] text-accent-foreground">
                Commercial
              </span>
            )}
          </div>
        )}
        {isFeatured && (
          <span className="absolute right-3 top-3 rounded-full bg-accent px-2.5 py-1 text-[10px] font-bold uppercase tracking-[1px] text-accent-foreground">
            {project.isSponsored ? 'Sponsored' : 'Featured'}
          </span>
        )}
      </div>

      <div className="p-4 sm:p-5">
        <h3 className="line-clamp-1 text-lg text-foreground">{project.title}</h3>

        {project.description && (
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-muted">
            {project.description}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2 text-xs text-muted">
            {mediaCount > 0 && (
              <span className="flex items-center gap-1.5">
                <Icon icon="cw-gallery" size="sm" />
                {mediaCount} {mediaCount === 1 ? 'photo' : 'photos'}
              </span>
            )}
            {mediaCount > 0 && year && <span aria-hidden="true">&middot;</span>}
            {year && <span>{year}</span>}
          </span>

          <span
            className={cn(
              'rounded-full px-4 py-2 text-xs font-medium transition-colors',
              'bg-surface-secondary text-foreground',
              'group-hover:bg-accent group-hover:text-accent-foreground'
            )}
          >
            View Project
          </span>
        </div>
      </div>
    </Link>
  )
}

export function PortfolioCardSkeleton({ count = 1 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ animationDelay: `${i * 0.06}s` }}
          className="overflow-hidden rounded-2xl border border-border bg-surface"
        >
          <Bone className="aspect-[16/10] w-full" />
          <div className="p-4 sm:p-5">
            <Bone className="h-6 w-3/4" />
            <div className="mt-1.5 space-y-1.5">
              <Bone className="h-3.5 w-full" />
              <Bone className="h-3.5 w-2/3" />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <Bone className="h-4 w-16" />
              <Bone className="h-8 w-28 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

// Kept as a static for existing call sites; loading.tsx must use the named
// export: a dotted static resolved through a Flight client reference races
// chunk loading on client navigation and renders undefined (React #130).
PortfolioCard.Skeleton = PortfolioCardSkeleton
